<template>
  <div class="campaign-product-list critical-component-clear">
    <div class="container">
      <badge-filter
        v-if="liquid.showFilter"
        show-filter
        :filter-options="filterOptions"
        :filters="filters"
        :sorting="liquid.filterSorting"
        :loading="initialLoad"
        prefix="-"
        suffix="%"
        :items-count="filteredProducts.length"
        total-items-label=""
        :no-result-message="$string('collection.no_products_filter_html')"
        @reset-filters="resetFilters"
        @toggle-filter="
          (payload) => toggleBadgeFilter(payload.key, payload.option)
        "
      />
      <product-grid
        v-if="filteredProducts.length || state === 'loading'"
        :campaign-products="filteredProducts"
        :campaign-loading="initialLoad"
        :discount-preview="discountPreview"
        :next-campaign-page="hasNextPage"
        :partial="partial"
        :products-per-page="productCount"
        template="campaign"
        :hide-soldout="liquid.hideSoldout"
        :is-filter-shown="liquid.showFilter"
        :handle-btn-click="getProductsByList"
        :parent-state="state"
        :infinite-scroll="liquid.infiniteScroll"
      />

      <div v-else class="campaign-product-list__empty-state text-center">
        <div
          v-if="liquid.title"
          class="campaign-product-list__title text-mobile-headlines-h4 text-desktop-headlines-h4"
          v-text="liquid.title"
        />

        <div
          v-if="liquid.text"
          class="campaign-product-list__text text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
          v-text="liquid.text"
        />

        <btn
          v-if="liquid.cta.text && liquid.cta.link"
          class="campaign-product-list__cta"
          :label="liquid.cta.text"
          modifiers="secondary block center"
          :url="liquid.cta.link"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Campaign product list (campaign-product-list)
 * -----------------------------------------------------------------------------
 * Campaign product list.
 *
 * @param {Array} discounts - Campaign metafield discounts.
 * @param {Boolean} discountPreview - Show preview of promoted campaign in
 * product card badges and prices.
 * @param {Boolean} initialPartial - Show only 4 products initially.
 * @param {String} liquid.cta -  Empty state - CTA text and URL.
 * @param {String} liquid.text -  Empty state - Section Text.
 * @param {String} liquid.title -  Empty state - Section Title.
 */

import { mapState } from 'pinia';
import { computeDiscountLogic } from '~/helpers/vue';

import Btn from '~global/btn/btn';
import ProductGrid from '~async/product-grid/product-grid';
import BadgeFilter from '~async/badge-filter/badge-filter';

import excludedNodesByIdQuery from '~graphql/excluded-nodes-by-id.gql';
import nodesByIdProductCardQuery from '~graphql/nodes-by-id-product-card.gql';

import {
  formatCampaign,
  formatCampaignProducts,
  formatExcludedCollection,
} from '~/format/format-campaign';
import { formatProduct } from '~/format/format-product';
import { decodeApiId, transformId } from '~/helpers/graphql';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'CampaignProductList',

  components: {
    ProductGrid,
    BadgeFilter,
    Btn,
  },

  provide() {
    return {
      variantsShowCount: this.liquid.variantsShowCount,
      variantsShowQatcOverlay: this.liquid.variantsShowQatcOverlay,
    };
  },

  props: {
    discounts: {
      type: Array,
      default: () => [],
    },
    discountPreview: {
      type: Boolean,
      default: true,
    },
    initialPartial: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        cta: {
          text: '',
          url: '',
        },
        productSorting: [],
        text: '',
        title: '',
        showFilter: false,
        filterSorting: 'Ascending',
        hideSoldout: false,
        productList: [],
        useCacheList: false,
        infiniteScroll: true,
        combineVariants: true,
        variantsShowCount: true,
        variantsShowQatcOverlay: true,
      }),
    },
    productCount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      allProducts: [],
      filterOptions: {},
      filters: {},
      removeFilters: {},
      initialLoad: false,
      partial: this.initialPartial,
      products: [],
      preparedCacheList: [],
      state: 'loading',
      productList: [],
      productsPerPage: {
        type: Number,
        default: 32,
      },
      pageNumber: 1,
      performanceMode: false,
      hasNextPage: false,
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      activeCampaign: 'active',
    }),

    /**
     * Compute contentList based on filters set.
     * @returns {String}
     */
    filteredProducts() {
      if ((this.state !== 'ready' && !this.performanceMode) || !this.products) {
        return [];
      }

      if (
        !this.filters?.Discount ||
        this.filters?.Discount?.length === 0 ||
        this.performanceMode
      ) {
        return this.products.filter(
          (product) =>
            !product.selectedOrFirstAvailableVariant?.compareAtPriceDiscount ||
            !this.filters?.Discount,
        );
      }

      return this.products?.filter((product) => {
        const productDiscount = this.getProductDiscount(product);

        if (
          this.filters.Discount.find(
            (discount) => discount === productDiscount.toString(),
          )
        ) {
          return product;
        }
        return false;
      });
    },
  },

  watch: {
    /**
     * Watch partial to update product list.
     */
    partial() {
      this.products = this.partial
        ? this.allProducts.slice(0, 4)
        : this.allProducts;
    },
  },

  async mounted() {
    cnvs.EventBus.listen('campaign:countdown-finished', () => {
      this.partial = false;
    });

    if (!this.discounts?.length) {
      this.state = 'ready';
      return;
    }

    /**
     * Get products.
     * - Store all products separately so they can be used when partial updates.
     */

    this.productList = this.liquid.productList || [];

    this.performanceMode = this.liquid.useCacheList && this.productList.length;

    if (this.performanceMode) {
      this.initialLoad = true;
      this.buildFilterOptions();
      this.parseURLParams();
      this.productsPerPage = this.$variable('settings.pagination.products');
      this.preparedCacheList = this.productList.map((product) => {
        return {
          productId: product.productId,
          variantId: product.variantId,
          isVariant: product.variantId,
          discount: product.discount,
        };
      });
      await this.getProductsByList();
    } else {
      const products = await this.getProducts();
      const productsWithoutDuplicates = this.deDuplicateProducts(products);

      this.allProducts =
        this.liquid.productSorting.length > 0
          ? this.sortProducts(
              productsWithoutDuplicates,
              this.liquid.productSorting,
            )
          : productsWithoutDuplicates;

      if (this.liquid.combineVariants) {
        this.allProducts = this.getCombinedVariants(false, this.allProducts);
      }

      this.products = this.partial
        ? this.allProducts.slice(0, 4)
        : this.allProducts;

      this.buildFilterOptions();
      this.parseURLParams();
    }

    this.state = 'ready';
  },

  methods: {
    /**
     * Get products from the discount campaign.
     * - Iterate through each campaign discount object to calculate include and
     *   excludes independently.
     * @returns {Array<Object>}
     */
    getProducts() {
      return new Promise(async (resolve, reject) => {
        try {
          let products = [];

          /**
           * Go through each discount object in campaign.
           */
          for (const object of this.discounts) {
            // eslint-disable-next-line no-await-in-loop
            const include = await this.getIncludeProducts(object);
            // eslint-disable-next-line no-await-in-loop
            const exclude = await this.getExcludeIds(object);

            /**
             * Create products list for this discount.
             */

            products.push(formatCampaignProducts(include, exclude));
            products = products.flat();
          }

          if (!products.length) {
            this.state = 'ready';
          }

          resolve(products);
        } catch (error) {
          cnvs.ReportError('Failed get campaign products', error);
          reject(error);
        }
      });
    },

    getProductsByList(keepPrevious = false) {
      this.state = 'loading';
      if (!keepPrevious) {
        this.products = [];
      }
      this.initialLoad = !keepPrevious;

      return new Promise(async (resolve, reject) => {
        try {
          const discountForRequest = this.filters.Discount
            ? this.filters.Discount.join()
            : '';

          const filteredList = this.preparedCacheList.filter(
            (product) =>
              !this.filters.Discount ||
              this.filters.Discount.includes(product.discount.toString()),
          );

          const ids = filteredList
            .slice(
              keepPrevious ? this.productsPerPage * this.pageNumber : 0,
              keepPrevious
                ? this.productsPerPage * (this.pageNumber + 1)
                : this.productsPerPage * this.pageNumber,
            )
            .map((product) => {
              return product.isVariant
                ? transformId(product.variantId, 'ProductVariant')
                : transformId(product.productId, 'Product');
            });

          /**
           * Load nodes from included IDs.
           */
          const response = await cnvs.Query({
            query: nodesByIdProductCardQuery,
            variables: {
              country: this.$variable('store.country'),
              ids,
              language: this.$variable('store.language'),
              limit: this.partial ? 4 : 250,
            },
          });

          const currentFilter = this.filters.Discount
            ? this.filters.Discount.join()
            : '';

          if (currentFilter !== discountForRequest) {
            resolve();
            return;
          }

          /**
           * Return unformatted nodes.
           */
          const products = formatCampaign(response)
            .map((node) => {
              if (node.variantNodeId) {
                return formatProduct(node, {
                  variantId: decodeApiId(node.variantNodeId),
                });
              }

              return formatProduct(node);
            })
            .flat();

          if (this.liquid.combineVariants) {
            this.allProducts = this.getCombinedVariants(keepPrevious, products);
          } else {
            this.allProducts = keepPrevious
              ? [...this.allProducts, ...products]
              : products;
          }

          this.pageNumber = keepPrevious
            ? this.pageNumber + 1
            : this.pageNumber;

          this.updateURL();

          this.products = this.partial
            ? this.allProducts.slice(0, 4)
            : this.allProducts;

          this.hasNextPage =
            filteredList.length > this.productsPerPage * this.pageNumber;
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to load included IDs', error);
          reject(error);
        }

        this.state = 'ready';
        await this.$nextTick();
        this.initialLoad = false;
      });
    },

    getCombinedVariants(keepPrevious, products) {
      let combinedProducts = keepPrevious ? [...this.allProducts] : [];

      products
        .filter(
          (product) =>
            !(product.isVariant && product.availableState === 'hidden'),
        )
        .forEach((product) => {
          const existingProduct = combinedProducts.find((addedProduct) => {
            return (
              addedProduct.id === product.id &&
              this.getProductDiscount(addedProduct) ===
                this.getProductDiscount(product)
            );
          });

          if (existingProduct) {
            combinedProducts = combinedProducts.map((oldProduct) => {
              if (
                oldProduct.id === product.id &&
                this.getProductDiscount(oldProduct) ===
                  this.getProductDiscount(product)
              ) {
                const existingVariant = oldProduct.variants.find(
                  (variant) =>
                    variant.id === product.selectedOrFirstAvailableVariant.id,
                );

                const updatedVariants = existingVariant
                  ? oldProduct.variants
                  : [
                      ...oldProduct.variants,
                      product.selectedOrFirstAvailableVariant,
                    ];

                return {
                  ...oldProduct,
                  isVariant: false,
                  variants: product.isVariant
                    ? updatedVariants
                    : product.variants,
                };
              }

              return oldProduct;
            });
          } else if (product.isVariant) {
            combinedProducts.push({
              ...product,
              variants: [product.selectedOrFirstAvailableVariant],
            });
          } else {
            combinedProducts.push(product);
          }
        });

      return combinedProducts;
    },

    /**
     * Get include products.
     * @param {Object} object - Campaign discount object.
     * @returns {Promise<Array>} - Unformatted product responses.
     */
    getIncludeProducts(object) {
      return new Promise(async (resolve, reject) => {
        try {
          let includeCollectionIds = [];
          let includeProductIds = [];
          let includeVariantIds = [];

          if (object.include?.collectionIds?.length) {
            includeCollectionIds = object.include.collectionIds.map((id) => {
              return transformId(id, 'Collection');
            });
          }

          if (object.include?.productIds?.length) {
            includeProductIds = object.include.productIds.map((id) => {
              return transformId(id, 'Product');
            });
          }

          if (object.include?.variantIds?.length) {
            includeVariantIds = object.include.variantIds.map((id) => {
              return transformId(id, 'ProductVariant');
            });
          }

          const ids = [
            ...includeCollectionIds,
            ...includeProductIds,
            ...includeVariantIds,
          ];

          /**
           * Load nodes from included IDs.
           */
          const response = await cnvs.Query({
            query: nodesByIdProductCardQuery,
            variables: {
              country: this.$variable('store.country'),
              ids,
              language: this.$variable('store.language'),
              limit: this.partial ? 4 : 250,
            },
          });

          /**
           * Return unformatted nodes.
           */
          resolve(formatCampaign(response));
        } catch (error) {
          cnvs.ReportError('Failed to load included IDs', error);
          reject(error);
        }
      });
    },

    /**
     * Get exclude IDs.
     * @param {Object} object - Campaign discount object.
     * @returns {Promise<Object>} - Separate product and variant IDs.
     */
    getExcludeIds(object) {
      return new Promise(async (resolve, reject) => {
        try {
          let excludeProductIds = [];
          let excludeVariantIds = [];

          if (object.exclude?.productIds?.length) {
            excludeProductIds = object.exclude.productIds.map((id) => {
              return transformId(id, 'Product');
            });
          }

          if (object.exclude?.variantIds?.length) {
            excludeVariantIds = object.exclude.variantIds.map((id) => {
              return transformId(id, 'ProductVariant');
            });
          }

          if (!object.exclude?.collectionIds?.length) {
            resolve({
              productIds: excludeProductIds,
              variantIds: excludeVariantIds,
            });

            return;
          }

          /**
           * Have to load excluded collection IDs to get excluded product IDs
           * from within the collection.
           */
          const excludeCollectionIds = object.exclude.collectionIds.map(
            (id) => {
              return transformId(id, 'Collection');
            },
          );

          const response = await cnvs.Query({
            query: excludedNodesByIdQuery,
            variables: {
              country: this.$variable('store.country'),
              ids: excludeCollectionIds,
              language: this.$variable('store.language'),
              limit: 250,
            },
          });

          const excludeCollectionProductIds =
            formatExcludedCollection(response);

          /**
           * Add excluded product IDs from excluded collections.
           */
          excludeProductIds = [
            ...excludeProductIds,
            ...excludeCollectionProductIds,
          ];

          resolve({
            productIds: excludeProductIds,
            variantIds: excludeVariantIds,
          });
        } catch (error) {
          cnvs.ReportError('Failed to load excluded IDs', error);
          reject(error);
        }
      });
    },

    /**
     * De-duplicate products.
     * @param {Array<Object>} products - All products.
     * @returns {Array<Object>}
     */
    deDuplicateProducts(products) {
      const uniqueIds = [];

      const uniqueProducts = products.filter((product) => {
        if (product.isVariant) {
          if (uniqueIds.includes(product.selectedOrFirstAvailableVariant.gid)) {
            return false;
          }

          uniqueIds.push(product.selectedOrFirstAvailableVariant.gid);
          return true;
        }

        if (uniqueIds.includes(product.gid)) {
          return false;
        }

        uniqueIds.push(product.gid);
        return true;
      });

      return uniqueProducts;
    },

    /**
     * Sort products based on Productvariant Ids.
     * @param {Array<Object>} products - All products.
     * @param {Array<Object>} ids - list with productvariant Ids.
     * @returns {Array<Object>}
     */
    sortProducts(products, ids) {
      const transformedIds = ids.map((id) => decodeApiId(id, 'ProductVariant'));
      const sortedProducts = [];

      transformedIds.forEach((id) => {
        const sortProductIndex = products.findIndex((product) => {
          const variantIds = product.variants.map((variant) => variant.id);
          return (
            (product.isVariant &&
              product.selectedOrFirstAvailableVariant.id === id) ||
            (!product.isVariant && variantIds.includes(id))
          );
        });

        /* if product for this sorting place is found add it to the sorted list,
          remove it from product list to avoid duplicates
        */
        if (sortProductIndex !== -1) {
          sortedProducts.push(products[sortProductIndex]);
          products.splice(sortProductIndex, 1);
        }
      });

      return sortedProducts.concat(products);
    },

    /**
     * Toggle filter.
     * @param {String} key - Filter key.
     * @param {String} value - Filter value.
     */
    async toggleBadgeFilter(key, value) {
      if (!this.filters[key]) {
        this.filters[key] = [];
      }

      this.filters[key] = [value];
      this.updateURL();

      if (this.performanceMode) {
        this.pageNumber = 1;
        await this.getProductsByList();
      }
    },

    /**
     * Reset all filters.
     */
    async resetFilters() {
      this.removeFilters = { ...this.filters };
      this.filters = {};

      if (this.performanceMode) {
        this.pageNumber = 1;
        await this.getProductsByList();
      }

      this.updateURL();
    },

    /**
     * Build filter options for campaign.
     */
    buildFilterOptions() {
      const filterOptions = new Set();

      if (this.performanceMode) {
        this.productList.forEach((product) => {
          const productDiscount = product.discount;
          if (productDiscount > 0) {
            filterOptions.add(productDiscount.toString());
          }
        });
      } else {
        this.products.forEach((product) => {
          const productDiscount = this.getProductDiscount(product);
          if (productDiscount > 0) {
            filterOptions.add(productDiscount.toString());
          }
        });
      }

      // on this page we will always have numbers as filter values, so sort them this way
      this.filterOptions = {
        Discount: Array.from(filterOptions).sort(
          (a, b) => parseFloat(a) - parseFloat(b),
        ),
      };
    },

    getProductDiscount(product) {
      const discountLogic = computeDiscountLogic({
        discountPreview: this.discountPreview,
        onSale: product.selectedOrFirstAvailableVariant?.compareAtPriceDiscount,
        product,
        variant: product.selectedOrFirstAvailableVariant,
      });
      const productDiscount = this.getCampaignDiscount(
        discountLogic,
        this.activeCampaign ? 'active' : 'promoted',
      );
      return productDiscount;
    },

    /**
     * Update URL. Reset own URL parameters while retaining other ones.
     */
    updateURL() {
      const url = new URL(window.location.href);
      // clear only our own URL parameters
      url.searchParams.delete('page');
      Object.keys(this.removeFilters).forEach((key) => {
        url.searchParams.delete(key);
      });
      this.removeFilters = {};

      if (this.pageNumber > 1) {
        url.searchParams.set('page', this.pageNumber);
      }

      // Add filters back with the right values if there are any
      Object.keys(this.filters).forEach((key) => {
        if (this.filters[key].length) {
          url.searchParams.set(key, this.filters[key].join(','));
        }
      });

      window.history.pushState({}, '', url);
    },

    /**
     * Parse URL parameters for the filters.
     */
    parseURLParams() {
      const urlParams = new URLSearchParams(window.location.search);

      this.pageNumber = parseInt(urlParams.get('page')) || 1;

      for (const [key, value] of urlParams.entries()) {
        // accept only valid filter keys
        if (key !== 'page') {
          if (key in this.filterOptions) {
            this.filters[key] = value.split(',');
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import './campaign-product-list';
</style>
